import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import VisibilitySensor from 'react-visibility-sensor';

import { iTag } from '../../../core/models/Tag';
import useGetTableColumns from './useGetTableColumns';
import useNewsTableFilters from './useNewsTableFilters';
import RoutesEnum from '../../../core/enums/RoutesEnum';
import { NewsArticleModel } from '../../../core/apiSchema';
import DataTable from '../../../components/DataTable/DataTable';
import NewsBrowseSection from '../NewsBrowseSection/NewsBrowseSection';
import NewsArchiveList from './NewsArchiveList/NewsArchiveList';
import { isEqualStrings, prepareForMatching } from '../../../core/helpers';
import useNewsArticlesWithFilters from '../../../core/apiHooks/useNewsArticlesWithFilters';
import StickyBrowseSection from '../../../components/DataTable/StickyBrowseSection/StickyBrowseSection';
import { BreakpointsEnum, useMediaQuery } from '../../../core/hooks/useMediaQuery';

interface iNewsTableProps {
    featuredArticleIds?: number[];
}

const NewsTable: React.FC<iNewsTableProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const isMobile = useMediaQuery(BreakpointsEnum.SM);

    const {
        filters: { orderBy, sortOrder, tagsIds, searchQuery },
        onSort,
        updateFilters
    } = useNewsTableFilters();

    const { data, isLoading, hasNextItems, loadNextPage, tagsAssociatedWithNewsArticles } = useNewsArticlesWithFilters({
        orderBy,
        sortOrder,
        tags: tagsIds,
        searchTerm: searchQuery
    });

    const toggleRowExpansion = useCallback((id: number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const onRowClick = useCallback(
        (item: NewsArticleModel) => {
            if (item?.id) {
                const returnUrl = location.pathname + location.search;

                navigate(RoutesEnum.NewsArticle.replace(':id', String(item.id)), { state: { returnUrl } });
            }
        },
        [navigate, location]
    );

    const onTagDrillDown = useCallback(
        (tag: iTag) => {
            const matchedTag = tagsAssociatedWithNewsArticles.find((t) => isEqualStrings(t.title, tag.title));

            if (!matchedTag?.id) return;

            const isAlreadyOnlySelectedTag = tagsIds?.length === 1 && tagsIds[0] === matchedTag.id;
            if (isAlreadyOnlySelectedTag) return;

            updateFilters({ tagsIds: [matchedTag.id] });
        },
        [tagsAssociatedWithNewsArticles, tagsIds, updateFilters]
    );

    const tagsLookupMap = useMemo(() => {
        return new Map(tagsAssociatedWithNewsArticles.map((t) => [prepareForMatching(t.title), t]));
    }, [tagsAssociatedWithNewsArticles]);

    const columns = useGetTableColumns({
        orderBy,
        sortOrder,
        onSort,
        expandedRows,
        toggleRowExpansion,
        searchQuery,
        onTagDrillDown,
        tagsLookupMap
    });

    return (
        <div className="News__results">
            <StickyBrowseSection
                BrowseSectionComponent={NewsBrowseSection}
                componentProps={{
                    tags: tagsAssociatedWithNewsArticles,
                    filters: {
                        tagsIds,
                        orderBy,
                        sortOrder,
                        searchQuery
                    },
                    onChange: updateFilters
                }}
            />
            {isMobile ? (
                <NewsArchiveList data={data} onItemClick={onRowClick} isLoading={isLoading} />
            ) : (
                <DataTable
                    data={data}
                    columns={columns}
                    onRowClick={onRowClick}
                    isLoading={isLoading}
                    emptyText="No results found"
                    showBottomLoader={data.length > 0 && isLoading}
                    showOverlayLoader={isLoading && data.length === 0}
                />
            )}
            <VisibilitySensor
                onChange={(visible: boolean) => {
                    if (visible && hasNextItems && !isLoading) {
                        loadNextPage();
                    }
                }}
                partialVisibility
                offset={{ bottom: -100 }}
            >
                {!isLoading || data.length > 0 ? <div className="Browse__sensor" /> : <div />}
            </VisibilitySensor>
        </div>
    );
};

export default NewsTable;
